import {
  Archive,
  Building2,
  CalendarRange,
  MapPin,
  Ruler,
  ClipboardList,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import type { CustomFieldId } from "@/lib/customFieldFilters";
import {
  PERSON_FILTER_FIELDS,
  type PersonFilterKey,
} from "./personFilterFields";

/** How archived opportunities are shown on the board. */
export type OpportunityArchiveMode = "active" | "archived" | "all";

/** A filter that can be added to the board: a person role, company, location, created date, the archive view, or a custom field. */
export type OpportunityFilterKey =
  | PersonFilterKey
  | "companyIds"
  | "locationIds"
  | "created"
  | "archive"
  | CustomFieldId;

/** A selectable filter type shown in the "Add filter" dropdown and as a chip. */
export interface OpportunityFilterType {
  key: OpportunityFilterKey;
  label: string;
  icon: LucideIcon;
}

const PERSON_ICONS: Record<PersonFilterKey, LucideIcon> = {
  salesRepUserIds: UserRound,
  estimatorUserIds: Ruler,
  taskAssigneeUserIds: ClipboardList,
};

/** All filter types available on the opportunity board, in display order. */
export const OPPORTUNITY_FILTER_TYPES: OpportunityFilterType[] = [
  ...PERSON_FILTER_FIELDS.map((field) => ({
    key: field.key,
    label: field.label,
    icon: PERSON_ICONS[field.key],
  })),
  { key: "companyIds", label: "Company", icon: Building2 },
  { key: "locationIds", label: "Location", icon: MapPin },
  { key: "created", label: "Created", icon: CalendarRange },
  { key: "archive", label: "Archive", icon: Archive },
];

/** The three archive-view choices offered by the Archive filter. */
export const ARCHIVE_MODE_OPTIONS: {
  value: OpportunityArchiveMode;
  label: string;
}[] = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
  { value: "all", label: "All" },
];

/** The rolling-window choices offered by the Created filter. */
export const CREATED_RANGE_PRESETS = [
  { value: "last30", label: "Last 30 days", days: 30 },
  { value: "last90", label: "Last 90 days", days: 90 },
] as const;

/** The identifier of one of the Created filter's rolling-window choices. */
export type CreatedRangePreset =
  (typeof CREATED_RANGE_PRESETS)[number]["value"];

/** The board's created-date selection; presets stay symbolic until resolved. */
export type OpportunityCreatedRange =
  | { kind: "preset"; preset: CreatedRangePreset }
  | { kind: "custom"; from: Date; to?: Date };

/**
 * Whether a filter key is one of the person-role filters.
 * @param key - The filter key to test
 * @returns True when the key is a person-role filter
 */
export function isPersonFilterKey(
  key: OpportunityFilterKey,
): key is PersonFilterKey {
  return PERSON_FILTER_FIELDS.some((field) => field.key === key);
}
